import SEO from "../seo/SEO";
import { 
  Shield, 
  Lock, 
  FileText, 
  Eye, 
  Cookie, 
  BarChart3, 
  HelpCircle, 
  ExternalLink, 
  AlertTriangle, 
  Mail, 
  Info,
  ChevronRight
} from "lucide-react";
import { Link } from "react-router-dom";

export default function PrivacyPolicy() {
  const sections = [
    { id: "about", title: "About USMoneyAI", icon: Info },
    { id: "collect", title: "Information We Collect", icon: Eye },
    { id: "cookies", title: "Cookies & Tracking", icon: Cookie },
    { id: "analytics", title: "Google Analytics", icon: BarChart3 },
    { id: "adsense", title: "Advertising & AdSense", icon: ExternalLink },
    { id: "thirdparty", title: "Third-Party Services & Links", icon: HelpCircle },
    { id: "security", title: "Data Security", icon: Lock },
    { id: "children", title: "Children's Privacy", icon: Shield },
    { id: "ccpa", title: "California Privacy Rights (CCPA/CPRA)", icon: Shield },
    { id: "gdpr", title: "European Economic Area (GDPR)", icon: Shield },
    { id: "disclaimer", title: "Financial Disclaimer", icon: AlertTriangle },
    { id: "changes", title: "Changes to This Policy", icon: FileText },
    { id: "contact", title: "Contact Us", icon: Mail },
  ];

  return (
    <div className="space-y-12 py-12 max-w-5xl mx-auto px-4">
      <SEO 
        title="Privacy Policy - USMoneyAI" 
        description="Learn how USMoneyAI collects, uses, protects, and handles your calculator inputs and browsing data under GDPR & CCPA standards."
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Privacy Policy", url: "/privacy" }
        ]}
      />

      {/* Header Banner */}
      <div className="space-y-4 text-left border-b border-slate-900 pb-8">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-amber-500/25 bg-amber-950/30 text-xs font-mono text-amber-400">
          <Shield className="w-3.5 h-3.5" />
          Consumer Privacy Protection
        </div>
        <h1 className="text-3xl sm:text-5xl font-display font-medium text-white tracking-tight">
          Privacy Policy
        </h1>
        <p className="text-slate-400 font-mono text-xs uppercase tracking-wider">
          Last Updated: June 2026
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Sticky Quick-Navigation Sidebar */}
        <div className="hidden lg:block lg:col-span-1">
          <div className="sticky top-24 space-y-4 bg-slate-950/10 border border-slate-900/50 rounded-2xl p-4">
            <h3 className="text-xs font-mono font-bold text-slate-400 uppercase tracking-widest px-2 mb-2">
              On This Page
            </h3>
            <nav className="space-y-1">
              {sections.map((sec) => (
                <a
                  key={sec.id}
                  href={`#${sec.id}`}
                  className="group flex items-center gap-2.5 px-2.5 py-2 text-xs font-medium text-slate-400 hover:text-amber-400 hover:bg-slate-900/40 rounded-lg transition-all duration-150"
                >
                  <sec.icon className="w-3.5 h-3.5 text-slate-500 group-hover:text-amber-400 transition-colors" />
                  <span className="truncate">{sec.title}</span>
                </a>
              ))}
            </nav>
          </div>
        </div>

        {/* Content Panel */}
        <div className="lg:col-span-3 space-y-10">
          
          {/* Welcome Text Card */}
          <div className="bg-slate-950/30 border border-slate-900 rounded-2xl p-6 sm:p-8 space-y-4 text-left">
            <h2 className="text-xl font-semibold text-white tracking-tight font-display">
              Welcome to USMoneyAI
            </h2>
            <p className="text-slate-300 font-light text-sm leading-relaxed">
              Welcome to USMoneyAI ("USMoneyAI", "we", "our", or "us"). We respect your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard information when you visit and use our website.
            </p>
            <p className="text-slate-300 font-light text-sm leading-relaxed">
              By using USMoneyAI, you agree to the terms outlined in this Privacy Policy.
            </p>
          </div>

          {/* Section: About USMoneyAI */}
          <section id="about" className="scroll-mt-24 space-y-4 text-left">
            <div className="flex items-center gap-2.5 border-b border-slate-900 pb-2">
              <div className="p-1.5 rounded-lg bg-amber-500/10 text-amber-400 border border-amber-500/20">
                <Info className="w-4 h-4" />
              </div>
              <h2 className="text-xl font-semibold text-white tracking-tight">
                About USMoneyAI
              </h2>
            </div>
            <p className="text-slate-300 font-light text-sm leading-relaxed">
              USMoneyAI provides free financial calculators, educational resources, and personal finance tools designed to help users better understand loans, mortgages, savings, investments, retirement planning, debt management, budgeting, and related financial topics.
            </p>
          </section>

          {/* Section: Information We Collect */}
          <section id="collect" className="scroll-mt-24 space-y-6 text-left">
            <div className="flex items-center gap-2.5 border-b border-slate-900 pb-2">
              <div className="p-1.5 rounded-lg bg-amber-500/10 text-amber-400 border border-amber-500/20">
                <Eye className="w-4 h-4" />
              </div>
              <h2 className="text-xl font-semibold text-white tracking-tight">
                Information We Collect
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-slate-950/20 border border-slate-900 p-5 rounded-xl space-y-3">
                <div className="text-xs font-mono font-bold text-amber-400 uppercase tracking-widest flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse"></span>
                  Information Collected Automatically
                </div>
                <p className="text-slate-300 font-light text-xs sm:text-sm leading-relaxed">
                  When you visit our website, certain information may be collected automatically, including:
                </p>
                <ul className="text-xs text-slate-400 space-y-1 list-disc pl-4 font-light">
                  <li>IP address</li>
                  <li>Browser type and version</li>
                  <li>Device information &amp; Operating system</li>
                  <li>Referring website link</li>
                  <li>Pages visited &amp; Date/time of visits</li>
                  <li>In-app usage statistics</li>
                </ul>
                <p className="text-xs text-slate-400 italic">
                  This information helps us improve website performance, security, and user experience.
                </p>
              </div>

              <div className="bg-slate-950/20 border border-slate-900 p-5 rounded-xl space-y-3">
                <div className="text-xs font-mono font-bold text-amber-400 uppercase tracking-widest flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
                  Information You Voluntarily Provide
                </div>
                <p className="text-slate-300 font-light text-xs sm:text-sm leading-relaxed">
                  We may collect information that you voluntarily provide to us, including:
                </p>
                <ul className="text-xs text-slate-400 space-y-1 list-disc pl-4 font-light">
                  <li>Contact form submissions</li>
                  <li>Direct email communications</li>
                  <li>Interactive feedback &amp; Feature inquiries</li>
                </ul>
                <p className="text-xs text-slate-400 italic">
                  We only collect information necessary to respond to your requests and improve our services.
                </p>
              </div>
            </div>
          </section>

          {/* Section: Cookies and Tracking Technologies */}
          <section id="cookies" className="scroll-mt-24 space-y-4 text-left">
            <div className="flex items-center gap-2.5 border-b border-slate-900 pb-2">
              <div className="p-1.5 rounded-lg bg-amber-500/10 text-amber-400 border border-amber-500/20">
                <Cookie className="w-4 h-4" />
              </div>
              <h2 className="text-xl font-semibold text-white tracking-tight">
                Cookies and Tracking Technologies
              </h2>
            </div>
            <p className="text-slate-300 font-light text-sm leading-relaxed">
              USMoneyAI may use cookies, web beacons, and similar technologies to:
            </p>
            <div className="bg-slate-950/10 border border-slate-900 rounded-xl p-4 sm:p-5">
              <ul className="space-y-2 text-slate-300 text-sm font-light">
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                  <span><strong>Improve Website Functionality:</strong> Activating calculators, persistence layers, and UI state indicators seamlessly.</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                  <span><strong>Remember User Preferences:</strong> Restoring specific fields or navigation presets between browser sessions safely.</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                  <span><strong>Analyze Traffic:</strong> Comprehending routing efficiency, loading speed metrics, and active calculator categories.</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                  <span><strong>Deliver Relevant Ads:</strong> Serving non-obtrusive, educational or fintech-oriented sponsorships that align with interest indicators.</span>
                </li>
              </ul>
            </div>
            <p className="text-slate-400 font-light text-xs leading-relaxed">
              You may disable cookies through your browser settings. However, certain features of our website may not function properly if cookies are disabled.
            </p>
          </section>

          {/* Section: Google Analytics */}
          <section id="analytics" className="scroll-mt-24 space-y-4 text-left">
            <div className="flex items-center gap-2.5 border-b border-slate-900 pb-2">
              <div className="p-1.5 rounded-lg bg-amber-500/10 text-amber-400 border border-amber-500/20">
                <BarChart3 className="w-4 h-4" />
              </div>
              <h2 className="text-xl font-semibold text-white tracking-tight">
                Google Analytics
              </h2>
            </div>
            <p className="text-slate-300 font-light text-sm leading-relaxed">
              We may use Google Analytics to understand how visitors interact with our website. Google Analytics may collect information such as:
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {["Pages Viewed", "Session Duration", "Device Information", "Geographic Region", "Traffic Sources"].map((item, index) => (
                <div key={index} className="bg-slate-950/20 border border-slate-900 rounded-lg p-3 text-center">
                  <span className="text-xs text-slate-300 font-medium font-sans">{item}</span>
                </div>
              ))}
            </div>
            <p className="text-slate-300 font-light text-sm leading-relaxed">
              This information helps us improve our content and services. For more information, please visit{" "}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:underline inline-flex items-center gap-0.5">
                Google's Privacy Policy <ExternalLink className="w-3 h-3 shrink-0" />
              </a>.
            </p>
          </section>

          {/* Section: Advertising and Google AdSense */}
          <section id="adsense" className="scroll-mt-24 space-y-4 text-left">
            <div className="flex items-center gap-2.5 border-b border-slate-900 pb-2">
              <div className="p-1.5 rounded-lg bg-amber-500/10 text-amber-400 border border-amber-500/20">
                <ExternalLink className="w-4 h-4" />
              </div>
              <h2 className="text-xl font-semibold text-white tracking-tight">
                Advertising and Google AdSense
              </h2>
            </div>
            <div className="space-y-3 text-slate-300 font-light text-sm leading-relaxed">
              <p>
                We may display advertisements through Google AdSense or similar advertising partners in the future.
              </p>
              <p>
                Third-party advertising vendors, including Google, may use cookies and similar technologies to serve ads based on your visits to this website and other websites.
              </p>
              <p>
                Google may use the DoubleClick cookie to display personalized advertisements based on user navigation activity.
              </p>
              <p>
                Users can manage ad personalization preferences through{" "}
                <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:underline inline-flex items-center gap-0.5">
                  Google's Ad Settings <ExternalLink className="w-3 h-3 shrink-0" />
                </a>.
              </p>
            </div>
          </section>

          {/* Section: Third-Party Services & Links */}
          <section id="thirdparty" className="scroll-mt-24 space-y-6 text-left">
            <div className="flex items-center gap-2.5 border-b border-slate-900 pb-2">
              <div className="p-1.5 rounded-lg bg-amber-500/10 text-amber-400 border border-amber-500/20">
                <HelpCircle className="w-4 h-4" />
              </div>
              <h2 className="text-xl font-semibold text-white tracking-tight">
                Third-Party Services &amp; Links
              </h2>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-white">Third-Party Services</h3>
                <p className="text-slate-300 font-light text-sm leading-relaxed">
                  We may use third-party tools and services, including:
                </p>
                <div className="flex flex-wrap gap-2 pt-1">
                  {["Analytics Providers", "Advertising Networks", "Website Performance Tools", "Security Services"].map((tool, idx) => (
                    <span key={idx} className="px-3 py-1 bg-slate-950/40 border border-slate-900 rounded-full text-xs text-slate-400 font-light">
                      {tool}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-slate-400 leading-relaxed italic">
                  These third parties may collect information according to their own privacy policies. We encourage users to verify them.
                </p>
              </div>

              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-white">Third-Party Links</h3>
                <p className="text-slate-300 font-light text-sm leading-relaxed">
                  Our website may contain links to external websites. We are not responsible for the privacy practices, content, or policies of third-party websites. Users should review the privacy policies of any external websites they visit.
                </p>
              </div>
            </div>
          </section>

          {/* Section: Data Security */}
          <section id="security" className="scroll-mt-24 space-y-4 text-left">
            <div className="flex items-center gap-2.5 border-b border-slate-900 pb-2">
              <div className="p-1.5 rounded-lg bg-amber-500/10 text-amber-400 border border-amber-500/20">
                <Lock className="w-4 h-4" />
              </div>
              <h2 className="text-xl font-semibold text-white tracking-tight">
                Data Security
              </h2>
            </div>
            <p className="text-slate-300 font-light text-sm leading-relaxed">
              We implement reasonable administrative, technical, and organizational measures to protect information from unauthorized access, disclosure, alteration, or destruction.
            </p>
            <p className="text-slate-300 font-light text-sm leading-relaxed">
              However, no method of internet transmission or electronic storage is completely secure. Therefore, we cannot guarantee absolute security.
            </p>
          </section>

          {/* Section: Children's Privacy */}
          <section id="children" className="scroll-mt-24 space-y-4 text-left">
            <div className="flex items-center gap-2.5 border-b border-slate-900 pb-2">
              <div className="p-1.5 rounded-lg bg-amber-500/10 text-amber-400 border border-amber-500/20">
                <Shield className="w-4 h-4" />
              </div>
              <h2 className="text-xl font-semibold text-white tracking-tight">
                Children's Privacy
              </h2>
            </div>
            <p className="text-slate-300 font-light text-sm leading-relaxed">
              USMoneyAI is not intended for children under the age of 13. We do not knowingly collect personal information from children. If we become aware that information from a child has been collected, we will take appropriate steps to remove it.
            </p>
          </section>

          {/* Section: California Privacy Rights */}
          <section id="ccpa" className="scroll-mt-24 space-y-4 text-left">
            <div className="flex items-center gap-2.5 border-b border-slate-900 pb-2">
              <div className="p-1.5 rounded-lg bg-amber-500/10 text-amber-400 border border-amber-500/20">
                <Shield className="w-4 h-4" />
              </div>
              <h2 className="text-xl font-semibold text-white tracking-tight">
                California Privacy Rights (CCPA/CPRA)
              </h2>
            </div>
            <p className="text-slate-300 font-light text-sm leading-relaxed">
              If you are a California resident, you may have certain rights regarding your personal information under applicable California privacy laws, including:
            </p>
            <div className="bg-slate-950/20 border border-slate-900 rounded-xl p-5 space-y-2">
              <ul className="text-sm text-slate-300 space-y-2 pl-4 list-disc font-light">
                <li><strong>The right to know</strong> which categories and specific pieces of personal information are collected and processed.</li>
                <li><strong>The right to request deletion</strong> of personal information collected from you.</li>
                <li><strong>The right to request correction</strong> of inaccurate personal information.</li>
                <li><strong>The right to limit</strong> certain uses and disclosures of sensitive personal information.</li>
              </ul>
            </div>
            <p className="text-slate-300 font-light text-sm leading-relaxed">
              To exercise these rights, please contact us through our{" "}
              <Link to="/contact" className="text-amber-400 hover:underline">
                Contact Us
              </Link>{" "}
              page.
            </p>
          </section>

          {/* Section: European Economic Area (GDPR) */}
          <section id="gdpr" className="scroll-mt-24 space-y-4 text-left">
            <div className="flex items-center gap-2.5 border-b border-slate-900 pb-2">
              <div className="p-1.5 rounded-lg bg-amber-500/10 text-amber-400 border border-amber-500/20">
                <Shield className="w-4 h-4" />
              </div>
              <h2 className="text-xl font-semibold text-white tracking-tight">
                European Economic Area (GDPR)
              </h2>
            </div>
            <p className="text-slate-300 font-light text-sm leading-relaxed">
              Visitors located in the European Economic Area may have additional rights under the General Data Protection Regulation (GDPR), including:
            </p>
            <div className="bg-slate-950/20 border border-slate-900 rounded-xl p-5 space-y-2 text-sm text-slate-300 font-light">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <h4 className="font-semibold text-white">Right of Access &amp; Correction</h4>
                  <p className="text-xs text-slate-400">Request a copy of your records and request updates to incorrect parameters.</p>
                </div>
                <div className="space-y-1">
                  <h4 className="font-semibold text-white">Right to Erasure (Forget Me)</h4>
                  <p className="text-xs text-slate-400">Request complete deletion of voluntarily submitted contact or telemetry profiles.</p>
                </div>
                <div className="space-y-1">
                  <h4 className="font-semibold text-white">Right to Restrict &amp; Object</h4>
                  <p className="text-xs text-slate-400">Withdraw processing clearance or halt specific tracking tags easily.</p>
                </div>
                <div className="space-y-1">
                  <h4 className="font-semibold text-white">Right to Data Portability</h4>
                  <p className="text-xs text-slate-400">Request secure exportation of logs in standard, readable formats.</p>
                </div>
              </div>
            </div>
            <p className="text-slate-300 font-light text-sm leading-relaxed">
              Requests regarding GDPR rights may be submitted through our{" "}
              <Link to="/contact" className="text-amber-400 hover:underline">
                Contact Us
              </Link>{" "}
              page.
            </p>
          </section>

          {/* Section: Financial Disclaimer */}
          <section id="disclaimer" className="scroll-mt-24 space-y-4 text-left">
            <div className="flex items-center gap-2.5 border-b border-slate-900 pb-2">
              <div className="p-1.5 rounded-lg bg-red-500/10 text-red-400 border border-red-500/20">
                <AlertTriangle className="w-4 h-4" />
              </div>
              <h2 className="text-xl font-semibold text-white tracking-tight">
                Financial Disclaimer
              </h2>
            </div>
            <p className="text-slate-300 font-light text-sm leading-relaxed">
              The calculators, tools, articles, and resources provided on USMoneyAI are for informational and educational purposes only.
            </p>
            <p className="text-slate-300 font-light text-sm leading-relaxed">
              Calculator results are estimates based on the information entered by users and should not be considered financial, investment, tax, legal, or professional advice.
            </p>
            <p className="text-slate-400 font-light text-xs leading-relaxed italic">
              Users should consult qualified professionals before making important financial decisions.
            </p>
          </section>

          {/* Section: Changes to This Privacy Policy */}
          <section id="changes" className="scroll-mt-24 space-y-4 text-left">
            <div className="flex items-center gap-2.5 border-b border-slate-900 pb-2">
              <div className="p-1.5 rounded-lg bg-amber-500/10 text-amber-400 border border-amber-500/20">
                <FileText className="w-4 h-4" />
              </div>
              <h2 className="text-xl font-semibold text-white tracking-tight">
                Changes to This Privacy Policy
              </h2>
            </div>
            <p className="text-slate-300 font-light text-sm leading-relaxed">
              We may update this Privacy Policy periodically to reflect changes in our practices, legal requirements, or website functionality. Any updates will be posted on this page with a revised "Last Updated" date.
            </p>
          </section>

          {/* Section: Contact Us */}
          <section id="contact" className="scroll-mt-24 space-y-4 text-left">
            <div className="flex items-center gap-2.5 border-b border-slate-900 pb-2">
              <div className="p-1.5 rounded-lg bg-amber-500/10 text-amber-400 border border-amber-500/20">
                <Mail className="w-4 h-4" />
              </div>
              <h2 className="text-xl font-semibold text-white tracking-tight">
                Contact Us
              </h2>
            </div>
            <p className="text-slate-300 font-light text-sm leading-relaxed">
              If you have questions regarding this Privacy Policy or our privacy practices, please contact us through our{" "}
              <Link to="/contact" className="text-amber-400 hover:underline inline-flex items-center gap-0.5 font-medium">
                Contact Us <ChevronRight className="w-3.5 h-3.5" />
              </Link>{" "}
              page.
            </p>
            
            <div className="bg-slate-950/40 border border-slate-900 rounded-xl p-5 space-y-3">
              <div className="text-sm font-semibold text-white">USMoneyAI Headquarters</div>
              <div className="text-xs text-slate-400 space-y-1 font-light leading-relaxed">
                <div>Website: <a href="https://usmoneyai.com" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:underline">https://usmoneyai.com</a></div>
                <div>Compliance Desk Address: USMoneyAI Team</div>
              </div>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
}
